import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ListContainer from '../components/ListContainer';
import API_BASE_URL from '../config/api';

const TransactionListPage = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Toast Notification State
  const [toastMessage, setToastMessage] = useState('');

  // Check for Toast message passed from Add/Edit Transaction Page
  useEffect(() => {
    if (location.state?.toastMessage) {
      setToastMessage(location.state.toastMessage);
      // Clear location state to avoid showing toast on manual refresh
      window.history.replaceState({}, document.title);
      const timer = setTimeout(() => {
        setToastMessage('');
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [location]);

  // Filter States
  const [startDateInput, setStartDateInput] = useState('');
  const [endDateInput, setEndDateInput] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Active Query States (sent to API on Search click)
  const [activeStartDate, setActiveStartDate] = useState('');
  const [activeEndDate, setActiveEndDate] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Pagination & Data States
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Fetch Transactions from API
  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const societyId = user?.society ? user.society._id : '';
      const queryParams = new URLSearchParams({
        societyId,
        page,
        limit,
        search: searchQuery.trim(),
        startDate: activeStartDate,
        endDate: activeEndDate
      });

      const res = await fetch(`${API_BASE_URL}/api/transactions?${queryParams.toString()}`);
      const data = await res.json();

      if (data.success) {
        setTransactions(data.transactions || []);
        setTotalCount(data.totalCount || 0);
        setTotalPages(data.totalPages || 1);
      }
    } catch (err) {
      console.error('Error fetching transactions:', err);
    } finally {
      setLoading(false);
    }
  }, [user, page, limit, searchQuery, activeStartDate, activeEndDate]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // Handle Search Click (Applies Search text + Date Range filters together)
  const handleSearchSubmit = () => {
    setSearchQuery(searchInput);
    setActiveStartDate(startDateInput);
    setActiveEndDate(endDateInput);
    setPage(1);
  };

  // Handle Clear Filters Click
  const handleClearFilters = () => {
    setSearchInput('');
    setStartDateInput('');
    setEndDateInput('');
    setSearchQuery('');
    setActiveStartDate('');
    setActiveEndDate('');
    setPage(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  // Format Date Function (e.g., 2025-11-11 -> 11-Nov-2025)
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = String(date.getDate()).padStart(2, '0');
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Export CSV Handler (Downloads all eligible records matching current filter)
  const handleExportCSV = async () => {
    if (totalCount === 0 && transactions.length === 0) return;
    try {
      const societyId = user?.society ? user.society._id : '';
      const queryParams = new URLSearchParams({
        societyId,
        page: 1,
        limit: Math.max(totalCount, 100000),
        search: searchQuery.trim(),
        startDate: activeStartDate,
        endDate: activeEndDate
      });

      const res = await fetch(`${API_BASE_URL}/api/transactions?${queryParams.toString()}`);
      const data = await res.json();
      const exportTx = (data.success && data.transactions) ? data.transactions : transactions;

      if (exportTx.length === 0) return;
    
      const headers = ["Date", "Farmer Name", "Father Name", "Mobile No", "Village", "Profile", "Machine", "Hrs", "Charges/Hr", "Machine Rental", "Tractor Required", "Tractor Rental", "Total Rental"];
      const rows = [];

      exportTx.forEach(t => {
        (t.Machines || []).forEach(m => {
          rows.push([
            `"${formatDate(t.TransactionDate)}"`,
            `"${t.FarmerName || ''}"`,
            `"${t.FatherName || ''}"`,
            `"${t.FarmerMobileNo || ''}"`,
            `"${t.Village || ''}"`,
            `"${t.FarmerProfile || ''}"`,
            `"${m.Machine || ''}"`,
            m.MachineHrs || 0,
            m.ChargesPerHrs || 0,
            m.MachineRental || 0,
            m.TractorRequired ? 'Yes' : 'No',
            m.TractorRental || 0,
            m.TotalRental || 0
          ].join(','));
        });
      });

      const csvContent = "data:text/csv;charset=utf-8," + [headers.join(','), ...rows].join('\n');
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `Transactions_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error exporting CSV:', err);
    }
  };

  // Render Filter Bar
  const filterBar = (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
        {/* Search Input */}
        <div style={{ position: 'relative', width: '240px' }}>
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search mobile, farmer, village..."
            style={{
              width: '100%',
              padding: '8px 12px 8px 32px',
              borderRadius: '8px',
              border: '1px solid #cbd5e1',
              fontSize: '13px',
              outline: 'none'
            }}
          />
          <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>
            🔍
          </span>
        </div>

        {/* Date Range Inputs */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <input
            type="date"
            value={startDateInput}
            onChange={(e) => setStartDateInput(e.target.value)}
            onKeyDown={handleKeyDown}
            style={{ padding: '7px 10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
          />
          <span style={{ color: '#94a3b8', fontSize: '12px' }}>to</span>
          <input
            type="date"
            value={endDateInput}
            onChange={(e) => setEndDateInput(e.target.value)}
            onKeyDown={handleKeyDown}
            style={{ padding: '7px 10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
          />
        </div>

        {/* Search & Clear Action Buttons */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button
            type="button"
            onClick={handleSearchSubmit}
            style={{
              background: '#f59e0b',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 18px',
              fontWeight: '600',
              fontSize: '13px',
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            Search
          </button>
          <button
            type="button"
            onClick={handleClearFilters}
            style={{
              background: '#f1f5f9',
              color: '#475569',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
              padding: '8px 16px',
              fontWeight: '600',
              fontSize: '13px',
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            Clear
          </button>
        </div>
      </div>

      {/* Export CSV Button */}
      <button
        type="button"
        onClick={handleExportCSV}
        disabled={transactions.length === 0}
        style={{
          background: transactions.length > 0 ? '#10b981' : '#cbd5e1',
          color: '#ffffff',
          border: 'none',
          borderRadius: '8px',
          padding: '8px 16px',
          fontWeight: '600',
          fontSize: '13px',
          cursor: transactions.length > 0 ? 'pointer' : 'not-allowed',
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}
      >
        📊 Export CSV
      </button>
    </div>
  );

  // Render Footer Pagination
  const footer = (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ fontSize: '13px', color: '#64748b' }}>
        Showing <b>{transactions.length > 0 ? (page - 1) * limit + 1 : 0}</b> to <b>{Math.min(page * limit, totalCount)}</b> of <b>{totalCount}</b> entries
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div className="rows-per-page-container" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#64748b' }}>
          <span>Rows per page:</span>
          <select
            value={limit}
            onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
            style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>

        <div style={{ display: 'flex', gap: '6px' }}>
          <button
            type="button"
            onClick={() => setPage(p => Math.max(p - 1, 1))}
            disabled={page === 1 || loading}
            style={{
              padding: '6px 12px',
              borderRadius: '6px',
              border: '1px solid #cbd5e1',
              background: page === 1 ? '#f1f5f9' : '#ffffff',
              color: page === 1 ? '#94a3b8' : '#1e293b',
              fontSize: '13px',
              cursor: page === 1 ? 'not-allowed' : 'pointer'
            }}
          >
            Previous
          </button>

          <span style={{ padding: '6px 12px', fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>
            Page {page} of {totalPages}
          </span>

          <button
            type="button"
            onClick={() => setPage(p => Math.min(p + 1, totalPages))}
            disabled={page >= totalPages || loading}
            style={{
              padding: '6px 12px',
              borderRadius: '6px',
              border: '1px solid #cbd5e1',
              background: page >= totalPages ? '#f1f5f9' : '#ffffff',
              color: page >= totalPages ? '#94a3b8' : '#1e293b',
              fontSize: '13px',
              cursor: page >= totalPages ? 'not-allowed' : 'pointer'
            }}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {toastMessage && (
        <div className="toast-notification">
          <span>✅</span> {toastMessage}
        </div>
      )}
      <ListContainer
        title="Transaction List"
        onClose={() => navigate('/home')}
        filterBar={filterBar}
        footer={footer}
      >
      <div style={{ overflowX: 'auto', maxWidth: '100%', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left', background: '#ffffff' }}>
          <thead>
            <tr style={{ background: '#ffffff', borderBottom: '2px solid #e2e8f0', color: '#334155', fontWeight: '700' }}>
              <th style={{ padding: '14px 16px', width: '120px', verticalAlign: 'top' }}>Date</th>
              <th style={{ padding: '14px 16px', width: '240px', verticalAlign: 'top' }}>Farmer Details</th>
              <th style={{ padding: '14px 16px', textAlign: 'center', verticalAlign: 'top' }}>Details of Implements rented</th>
              <th style={{ padding: '14px 16px', width: '60px', textAlign: 'center', verticalAlign: 'top' }}>Edit</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              /* Skeleton Shimmer Loading Rows */
              Array.from({ length: 5 }).map((_, idx) => (
                <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                  <td style={{ padding: '16px' }}>
                    <div style={{ height: '16px', background: '#e2e8f0', borderRadius: '4px', width: '80px', animation: 'pulse 1.5s infinite' }}></div>
                  </td>
                  <td style={{ padding: '16px' }}>
                    <div style={{ height: '14px', background: '#e2e8f0', borderRadius: '4px', width: '140px', marginBottom: '8px' }}></div>
                    <div style={{ height: '14px', background: '#e2e8f0', borderRadius: '4px', width: '110px', marginBottom: '8px' }}></div>
                    <div style={{ height: '14px', background: '#e2e8f0', borderRadius: '4px', width: '90px' }}></div>
                  </td>
                  <td style={{ padding: '16px' }}>
                    <div style={{ height: '80px', background: '#e2e8f0', borderRadius: '6px', width: '100%' }}></div>
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    <div style={{ height: '24px', background: '#e2e8f0', borderRadius: '50%', width: '24px', margin: '0 auto' }}></div>
                  </td>
                </tr>
              ))
            ) : transactions.length === 0 ? (
              <tr>
                <td colSpan="4" style={{ padding: '32px', textAlign: 'center', color: '#94a3b8', fontStyle: 'italic' }}>
                  No transactions found matching your criteria.
                </td>
              </tr>
            ) : (
              transactions.map((t) => {
                const totalMachineHrs = (t.Machines || []).reduce((sum, m) => sum + (m.MachineHrs || 0), 0);
                const totalChargesPerHrs = (t.Machines || []).reduce((sum, m) => sum + (m.ChargesPerHrs || 0), 0);
                const totalMachineRental = (t.Machines || []).reduce((sum, m) => sum + (m.MachineRental || 0), 0);
                const totalTractorRental = (t.Machines || []).reduce((sum, m) => sum + (m.TractorRental || 0), 0);
                const totalTotalRental = (t.Machines || []).reduce((sum, m) => sum + (m.TotalRental || 0), 0);

                return (
                  <tr key={t._id} style={{ borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                    {/* Date */}
                    <td style={{ padding: '16px', verticalAlign: 'top', fontWeight: '500', color: '#475569', whiteSpace: 'nowrap' }}>
                      {formatDate(t.TransactionDate)}
                    </td>

                    {/* Farmer Details */}
                    <td style={{ padding: '16px', verticalAlign: 'top', color: '#334155', lineHeight: '1.6' }}>
                      <div><b>Farmer Name:</b> <span style={{ color: '#64748b' }}>{t.FarmerName}</span></div>
                      <div><b>Village:</b> <span style={{ color: '#64748b' }}>{t.Village}</span></div>
                      <div><b>Mobile No:</b> <span style={{ color: '#64748b' }}>{t.FarmerMobileNo}</span></div>
                      <div><b>Father Name:</b> <span style={{ color: '#64748b' }}>{t.FatherName}</span></div>
                      <div><b>Profile:</b> <span style={{ color: '#64748b' }}>{t.FarmerProfile}</span></div>
                    </td>

                    {/* Details of Implements Rented Sub-table */}
                    <td style={{ padding: '12px 16px', verticalAlign: 'top' }}>
                      <div style={{ overflowX: 'auto', border: '1px solid #cbd5e1', borderRadius: '4px', background: '#ffffff' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', textAlign: 'left' }}>
                          <thead>
                            <tr style={{ background: '#f1f5f9', borderBottom: '1px solid #cbd5e1', color: '#1e293b' }}>
                              <th style={{ padding: '8px 10px', minWidth: '130px' }}>ਮਸ਼ੀਨਾਂ<br/>(Machine(s))</th>
                              <th style={{ padding: '8px 10px', width: '90px', textAlign: 'right' }}>ਘੰਟੇ) ਕਿਰਾਇਆ<br/>(Hour(s) Rented)</th>
                              <th style={{ padding: '8px 10px', width: '110px', textAlign: 'right' }}>ਘੰਟੇ ਪ੍ਰਤੀ ਕਿਰਾਇਆ ਕਿਰਾਏ<br/>(Rental Charges Per Hour)</th>
                              <th style={{ padding: '8px 10px', width: '100px', textAlign: 'right' }}>ਕਿਰਾਏ ਦਾ ਖਰਚਾ<br/>(Rental Charges)</th>
                              <th style={{ padding: '8px 10px', width: '100px', textAlign: 'center' }}>ਟਰੈਕਟਰ ਲੋੜੀਂਦਾ<br/>(Tractor Required)</th>
                              <th style={{ padding: '8px 10px', width: '100px', textAlign: 'right' }}>ਟਰੈਕਟਰ ਕਿਰਾਇਆ<br/>(Tractor Rental)</th>
                              <th style={{ padding: '8px 10px', width: '90px', textAlign: 'right' }}>ਕੁਲ ਕਿਰਾਇਆ<br/>(Total Rental)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(t.Machines || []).map((m, idx) => (
                              <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                <td style={{ padding: '8px 10px', fontWeight: '500', color: '#475569' }}>{m.Machine}</td>
                                <td style={{ padding: '8px 10px', textAlign: 'right' }}>{m.MachineHrs}</td>
                                <td style={{ padding: '8px 10px', textAlign: 'right' }}>{m.ChargesPerHrs}</td>
                                <td style={{ padding: '8px 10px', textAlign: 'right' }}>{m.MachineRental}</td>
                                <td style={{ padding: '8px 10px', textAlign: 'center' }}>{m.TractorRequired ? 'Yes' : 'No'}</td>
                                <td style={{ padding: '8px 10px', textAlign: 'right' }}>{m.TractorRental}</td>
                                <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: '600' }}>{m.TotalRental}</td>
                              </tr>
                            ))}
                            {/* Grand Total Row */}
                            <tr style={{ background: '#f8fafc', fontWeight: '700', color: '#1e293b' }}>
                              <td style={{ padding: '8px 10px' }}>Grand Total</td>
                              <td style={{ padding: '8px 10px', textAlign: 'right' }}>{totalMachineHrs}</td>
                              <td style={{ padding: '8px 10px', textAlign: 'right' }}>{totalChargesPerHrs}</td>
                              <td style={{ padding: '8px 10px', textAlign: 'right' }}>{totalMachineRental}</td>
                              <td style={{ padding: '8px 10px', textAlign: 'center' }}>-</td>
                              <td style={{ padding: '8px 10px', textAlign: 'right' }}>{totalTractorRental}</td>
                              <td style={{ padding: '8px 10px', textAlign: 'right' }}>{totalTotalRental}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </td>

                    {/* Edit Action Button */}
                    <td style={{ padding: '16px', verticalAlign: 'top', textAlign: 'center' }}>
                      <button
                        type="button"
                        onClick={() => navigate('/add-transaction', { state: { transaction: t } })}
                        title="Edit Transaction"
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '18px',
                          color: '#475569'
                        }}
                      >
                        📝
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </ListContainer>
    </>
  );
};

export default TransactionListPage;
